import { z } from "zod";

export const routeSchema = z.object({
  origin: z.string().min(3, "Required").max(3, "3 letters").toUpperCase(),
  destination: z.string().min(3, "Required").max(3, "3 letters").toUpperCase(),
  departureDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD"),
  timeConstraint: z
    .object({
      type: z.enum(["any", "before", "after"]),
      time: z
        .string()
        .regex(/^\d{2}:\d{2}$/)
        .optional(),
    })
    .optional()
    .default({ type: "any" }),
  position: z.number().int().min(0),
  earliest_departure: z
    .string()
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, "HH:MM format")
    .optional()
    .or(z.literal("")),
  latest_arrival: z
    .string()
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, "HH:MM format")
    .optional()
    .or(z.literal("")),
  direct_flights_only: z.boolean().optional().default(false),
  preferred_airlines: z.array(z.string()).optional().default([]),
  // true when the Coach guessed this leg's origin (no origin stated) — flagged in the UI.
  origin_assumed: z.boolean().optional().default(false),
});

export const preferencesSchema = z.object({
  direct_only: z.boolean().default(false),
  prefer_widebody: z.boolean().default(false),
  arrive_late: z.boolean().default(false),
  prefer_low_occupancy: z.boolean().default(false),
  prefer_changeable: z.boolean().default(false),
  carrier_filter: z
    .object({
      mode: z.enum(["include", "exclude"]).default("include"),
      carriers: z.array(z.string()).default([]),
    })
    .default({ mode: "include", carriers: [] }),
  price_sensitivity: z.number().min(10).max(200).default(50),
});

export const createTripSchema = z.object({
  name: z.string().trim().min(1, "Trip name is required").max(255, "Trip name too long"),
  description: z.string().max(1000, "Description too long").optional().or(z.literal("")),
  routes: z.array(routeSchema).min(1, "At least one route is required"),
  preferences: preferencesSchema.default({
    direct_only: false,
    prefer_widebody: false,
    arrive_late: false,
    prefer_low_occupancy: false,
    prefer_changeable: false,
    carrier_filter: { mode: "include", carriers: [] },
    price_sensitivity: 50,
  }),
  passengers: z.number().int().min(1).max(9).default(1),
});

export type CreateTripInput = z.infer<typeof createTripSchema>;
export type TripPreferences = z.infer<typeof preferencesSchema>;
